import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { UserPlus, UserX, Loader2, Search, CheckCircle2 } from 'lucide-react';
import { accountingAPI, agentAPI } from '@/lib/api';
import { toast } from 'sonner';

/**
 * Unmatched Agents bucket.
 *
 * Lists the statement agent NAMES that could not be resolved to an Agent
 * (carriers like Humana send no NPN). Assigning an agent REMEMBERS the choice
 * as a per-carrier alias, so this and future statements auto-resolve the same
 * name — even when the carrier writes it in a different word order.
 */
export function UnmatchedAgentsCard({ importId, onResolved }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignFor, setAssignFor] = useState(null);   // group being assigned
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!importId) return;
    setLoading(true);
    try {
      const { data } = await accountingAPI.unmatchedAgents(importId);
      setGroups(data.groups || []);
    } catch {
      setGroups([]);
    } finally { setLoading(false); }
  }, [importId]);

  useEffect(() => { load(); }, [load]);

  const openAssign = async (group) => {
    setAssignFor(group);
    setSearch('');
    if (agents.length === 0) {
      try {
        const { data } = await agentAPI.simpleList();
        setAgents(data || []);
      } catch { setAgents([]); }
    }
  };

  const filteredAgents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return agents.slice(0, 50);
    return agents.filter((a) =>
      (a.name || '').toLowerCase().includes(q) || String(a.npn).includes(q),
    ).slice(0, 50);
  }, [agents, search]);

  const assign = async (npn) => {
    setSaving(true);
    try {
      const { data } = await accountingAPI.assignAgent(importId, {
        display_name: assignFor?.display_name,
        carrier_agent_id: assignFor?.carrier_agent_id || undefined,
        agent_npn: String(npn),
      });
      toast.success(`"${assignFor?.display_name}" linked to ${data.agent_name} — remembered for this carrier`);
      setAssignFor(null);
      await load();
      onResolved?.(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign agent');
    } finally { setSaving(false); }
  };

  if (!loading && groups.length === 0) return null;

  return (
    <Card className="border-amber-200" data-testid="unmatched-agents-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 text-amber-800">
          <UserX size={16} /> Unmatched agents
          <Badge className="bg-amber-100 text-amber-800">{groups.length}</Badge>
        </CardTitle>
        <p className="text-xs text-slate-500">
          These statement names have no NPN and no remembered match. Assign the agent once —
          we&apos;ll remember it for this carrier so next month resolves automatically.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="py-6 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" size={18} /></div>
        ) : (
          <div className="divide-y divide-slate-100">
            {groups.map((g) => (
              <div key={g.group_key || g.alias_key || g.display_name} className="flex items-center justify-between py-2" data-testid={`unmatched-row-${g.group_key || g.alias_key}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm text-slate-800 truncate">{g.display_name}</div>
                  <div className="text-[11px] text-slate-400">
                    {g.line_count} line{g.line_count === 1 ? '' : 's'}
                    {g.carrier_agent_id && <> · carrier ID <span className="font-mono">{g.carrier_agent_id}</span></>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => openAssign(g)} data-testid={`assign-agent-btn-${g.group_key || g.alias_key}`}>
                  <UserPlus size={13} className="mr-1" /> Assign agent
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={!!assignFor} onOpenChange={(v) => { if (!v && !saving) setAssignFor(null); }}>
        <DialogContent className="max-w-md" data-testid="assign-agent-dialog">
          <DialogHeader>
            <DialogTitle>Assign agent</DialogTitle>
            <DialogDescription>
              Link <span className="font-medium text-slate-700">{assignFor?.display_name}</span> to one of your agents.
              {assignFor?.carrier_agent_id && <> (carrier ID <span className="font-mono">{assignFor.carrier_agent_id}</span>)</>}
              {' '}This will be remembered for this carrier.
            </DialogDescription>
          </DialogHeader>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or NPN…"
              className="pl-8"
              data-testid="assign-agent-search"
            />
          </div>
          <div className="mt-2 max-h-72 overflow-y-auto divide-y divide-slate-100">
            {filteredAgents.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-400">No agents found.</div>
            ) : filteredAgents.map((a) => (
              <button
                key={a.npn}
                onClick={() => assign(a.npn)}
                disabled={saving}
                className="w-full flex items-center justify-between py-2 px-1 text-left text-sm hover:bg-slate-50 disabled:opacity-50"
                data-testid={`assign-agent-option-${a.npn}`}
              >
                <span className="truncate">{a.name}</span>
                <span className="font-mono text-[11px] text-slate-400 ml-2">NPN {a.npn}</span>
              </button>
            ))}
          </div>
          {saving && (
            <div className="flex items-center gap-2 text-xs text-slate-500 pt-1">
              <Loader2 size={12} className="animate-spin" /> Linking & re-matching…
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default UnmatchedAgentsCard;
